import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkItem, WorkItemState } from '@reef/schema';
import { api, type FactorySummary, type StepRunSummary, type WorkItemRow } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { NewWorkItem } from '../components/NewWorkItem.tsx';
import { CompactRunTimeline } from '../components/RunTimeline.tsx';
import type { Route } from '../routes.ts';

/**
 * The board: every piece of work the caller can see, in the stage it is in.
 *
 * One column per stage of the chosen factory, plus the two exits — escalated
 * to a person, and rejected — because an item that left the lifecycle is
 * still an item somebody filed and it should not vanish from the only screen
 * that lists them. An item is placed by the state its run's steps wrote, so
 * the board is a fact about the run history and not a thing anybody drags.
 *
 * `/work/:runId` opens one item, and `/work/new` opens the form, so both are
 * links a person can send.
 */
const EXITS: Array<{ id: string; name: string; states: WorkItemState[] }> = [
  { id: 'escalated', name: 'Escalated', states: ['escalated'] },
  { id: 'rejected', name: 'Rejected', states: ['rejected'] },
];

export function WorkView({
  factories,
  runId,
  userId,
  go,
}: {
  factories: FactorySummary[];
  /** From the path: an item's run id, `new` for the form, null for the board alone. */
  runId: string | null;
  /** The session's reef user id — what "mine" compares a run's `triggeredBy` against. */
  userId: string;
  go(route: Partial<Route> & { tab: Route['tab'] }, replace?: boolean): void;
}) {
  const [rows, setRows] = useState<WorkItemRow[]>([]);
  const [hidden, setHidden] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const delivery = useMemo(() => factories.filter((factory) => factory.kind === 'delivery'), [factories]);
  const [factoryId, setFactoryId] = useState<string | null>(null);
  const [view, setView] = useState<WorkViewMode>(readViewMode);
  const [query, setQuery] = useState('');
  const [mine, setMine] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'updated', descending: true });

  const chooseView = (next: WorkViewMode) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // A browser that refuses storage still gets the view; it is just not remembered.
    }
  };

  const refresh = useCallback(() => {
    api
      .workItems()
      .then((result) => {
        setRows(result.items);
        setHidden(result.hidden);
        setLoaded(true);
      })
      .catch((caught) => setError((caught as Error).message));
  }, []);

  useEffect(refresh, [refresh]);

  const factory = delivery.find((entry) => entry.id === factoryId) ?? delivery[0] ?? null;
  const selected = runId && runId !== 'new' ? (rows.find((row) => row.runId === runId) ?? null) : null;

  /*
   * The filters narrow what both views draw; the drawer is deliberately not
   * behind them, because `/work/:runId` is a link somebody sent and the item it
   * names must open whether or not the reader's search box would have found it.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (mine && row.triggeredBy !== userId) return false;
      if (!needle) return true;
      return (
        row.item.title.toLowerCase().includes(needle) ||
        row.item.id.toLowerCase().includes(needle) ||
        row.runId.toLowerCase().includes(needle) ||
        (row.item.repo ?? '').toLowerCase().includes(needle)
      );
    });
  }, [rows, query, mine, userId]);

  const columns = useMemo(() => {
    if (!factory) return [];
    const stageColumns = factory.stages
      .filter((stage) => stage.states.length > 0)
      .map((stage) => ({
        id: stage.id,
        name: stage.name,
        states: stage.states,
        rows: shown.filter((row) => row.factoryId === factory.id && row.stageId === stage.id).sort(byUrgency),
      }));
    const exits = EXITS.map((exit) => ({
      ...exit,
      rows: shown
        .filter((row) => row.factoryId === factory.id && exit.states.includes(row.item.state))
        .sort(byUrgency),
    }));
    return [...stageColumns, ...exits];
  }, [factory, shown]);

  const unplaced = shown.filter((row) => !row.factoryId).sort(byUrgency);

  /*
   * The list is the board's rows in one table: the chosen factory's items and
   * the ones outside any factory, which the board draws in its own section
   * below. An item that shows on one view and not the other would read as the
   * toggle losing work.
   */
  const stageNames = useMemo(
    () => new Map((factory?.stages ?? []).map((stage) => [stage.id, stage.name] as const)),
    [factory],
  );
  const listed = useMemo(() => {
    const onBoard = shown.filter((row) => !row.factoryId || row.factoryId === factory?.id);
    const compare = SORTERS[sort.key](stageNames);
    return onBoard.sort((left, right) => (sort.descending ? -compare(left, right) : compare(left, right)));
  }, [shown, factory, sort, stageNames]);

  const sortBy = (key: SortKey) =>
    setSort((current) =>
      current.key === key ? { key, descending: !current.descending } : { key, descending: key === 'updated' },
    );

  return (
    <div className="page wide">
      <header className="page-head">
        <div>
          <h1>Work</h1>
          <p className="page-lede">
            Every piece of work in flight, in the stage it has reached. Items arrive from the
            tracker on their own, or from the button on the right.
          </p>
        </div>
        <div className="row">
          {delivery.length > 1 && (
            <select
              aria-label="Factory"
              value={factory?.id ?? ''}
              onChange={(event) => setFactoryId(event.target.value)}
              style={{ width: 'auto' }}
            >
              {delivery.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          )}
          {/*
            Board or list, beside the picker because both decide what the page
            below is. Remembered per browser: a board of forty is a list every
            time for the person triaging it, and choosing again on every visit
            is the kind of friction that sends them back to the tracker.
          */}
          <div className="toggle" role="group" aria-label="View">
            <button aria-pressed={view === 'board'} onClick={() => chooseView('board')}>
              Board
            </button>
            <button aria-pressed={view === 'list'} onClick={() => chooseView('list')}>
              List
            </button>
          </div>
          <button className="ghost" onClick={refresh}>
            Refresh
          </button>
          <button className="primary" onClick={() => go({ tab: 'work', runId: 'new' })}>
            <Icon name="plus" /> New work item
          </button>
        </div>
      </header>

      {error && <div className="banner danger">{error}</div>}

      {rows.length > 0 && runId !== 'new' && (
        <div className="board-filters">
          <input
            type="search"
            aria-label="Search work"
            placeholder="Search by title, id or repo"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {/*
            "Mine" is the run's `triggeredBy`, not the item's `updatedBy`: the
            latter is the last step to write the item and is almost never a
            person, so a filter on it would show nobody anything.
          */}
          <label className="row">
            <input type="checkbox" checked={mine} onChange={(event) => setMine(event.target.checked)} />
            Mine
          </label>
          {shown.length !== rows.length && (
            <span className="hint">
              {shown.length} of {rows.length}
            </span>
          )}
        </div>
      )}

      {runId === 'new' && (
        <section className="panel">
          <header className="panel-head">
            <h2>Describe the work</h2>
          </header>
          <NewWorkItem
            factories={factories}
            initialFactoryId={factory?.id ?? null}
            onStarted={(startedRun) => {
              refresh();
              go({ tab: 'work', runId: startedRun }, true);
            }}
            onCancel={() => go({ tab: 'work' }, true)}
          />
        </section>
      )}

      {loaded && rows.length === 0 && runId !== 'new' && (
        <div className="empty-state">
          <h3>Nothing is in flight</h3>
          <p>
            A work item appears here the moment a run carries one — a Linear issue moving into
            Triage, a signal from monitoring, or something you describe yourself.
          </p>
          <button className="primary" onClick={() => go({ tab: 'work', runId: 'new' })}>
            <Icon name="plus" /> New work item
          </button>
        </div>
      )}

      {rows.length > 0 && factory && view === 'board' && (
        <div className="board-wrap">
          {/*
            The column minimum is a variable so the stylesheet can lower it on
            a laptop; a literal here would win over any media query and keep
            five 220px columns scrolling inside a 900px window.
          */}
          <div
            className="board"
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(var(--board-col-min, 220px), 1fr))` }}
          >
            {columns.map((column) => {
              const waiting = column.rows.filter((row) => row.runStatus === 'awaiting_approval').length;
              return (
              <section key={column.id} className="board-column">
                <header>
                  <span>{column.name}</span>
                  {/*
                    How many of these are waiting on a person, in the header
                    rather than only as a dot on each card: the question a
                    reader brings to a column is "is anything here stuck on
                    me", and the count answers it without scanning the cards.
                  */}
                  {waiting > 0 && (
                    <span className="chip warn" title={`${waiting} waiting on a person`}>
                      {waiting} waiting
                    </span>
                  )}
                  <span className="rail-count">{column.rows.length}</span>
                </header>
                <div className="board-cards">
                  {column.rows.map((row) => (
                    <WorkCard
                      key={row.runId}
                      row={row}
                      selected={selected?.runId === row.runId}
                      onOpen={() => go({ tab: 'work', runId: row.runId }, true)}
                    />
                  ))}
                  {column.rows.length === 0 && <p className="board-empty">—</p>}
                </div>
              </section>
              );
            })}
          </div>
        </div>
      )}

      {/*
        * Not gated on a chosen factory, unlike the board above. The board is
        * that factory's columns and has nothing to draw without one; the list
        * is the same rows in a table, and it already includes the items outside
        * every factory — which is all of them in a deployment that ships none.
        * Gated, the List toggle rendered a blank screen and read as the view
        * having lost the work the board had just shown.
        */}
      {rows.length > 0 && view === 'list' && (
        <section className="panel">
          {listed.length === 0 ? (
            <p className="panel-quiet">Nothing matches.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="studio-table work-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        aria-sort={
                          sort.key === column.key ? (sort.descending ? 'descending' : 'ascending') : undefined
                        }
                      >
                        <button className="sort" onClick={() => sortBy(column.key)}>
                          {column.label}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listed.map((row) => (
                    <tr
                      key={row.runId}
                      tabIndex={0}
                      aria-current={selected?.runId === row.runId ? 'true' : undefined}
                      onClick={() => go({ tab: 'work', runId: row.runId }, true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          go({ tab: 'work', runId: row.runId }, true);
                        }
                      }}
                    >
                      <td>
                        <strong>{row.item.title}</strong>
                        <div className="hint mono">{row.item.id}</div>
                      </td>
                      <td>
                        {row.stageId && stageNames.has(row.stageId) ? (
                          stageNames.get(row.stageId)
                        ) : (
                          <span className="hint">outside any factory</span>
                        )}
                        <div className="hint">{row.workflowName}</div>
                      </td>
                      <td>
                        {row.item.priority ? (
                          <span className={`pill ${priorityTone(row.item.priority)}`}>{row.item.priority}</span>
                        ) : (
                          <span className="hint">—</span>
                        )}
                      </td>
                      <td>
                        <span className="run-status">
                          <span className={`run-dot ${runTone(row.runStatus)}`} aria-hidden="true" />
                          {row.runStatus.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="mono hint">{row.item.repo ?? '—'}</td>
                      <td className="hint">{timeAgo(row.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {shown.length === 0 && rows.length > 0 && view === 'board' && (
        <p className="hint">Nothing matches the filters.</p>
      )}

      {unplaced.length > 0 && view === 'board' && (
        <section className="panel">
          <header className="panel-head">
            <h2>Outside any factory</h2>
            <span className="hint">Carried by a workflow no delivery factory names.</span>
          </header>
          <div className="board-cards row-cards">
            {unplaced.map((row) => (
              <WorkCard
                key={row.runId}
                row={row}
                selected={selected?.runId === row.runId}
                onOpen={() => go({ tab: 'work', runId: row.runId }, true)}
              />
            ))}
          </div>
        </section>
      )}

      {hidden > 0 && (
        <p className="hint">
          {hidden} item{hidden === 1 ? '' : 's'} on workflows you are not shared on {hidden === 1 ? 'is' : 'are'} not shown.
        </p>
      )}

      {selected && (
        <WorkItemDrawer
          key={selected.runId}
          row={selected}
          onClose={() => go({ tab: 'work' }, true)}
          onRunChanged={refresh}
          go={go}
        />
      )}
    </div>
  );
}

type WorkViewMode = 'board' | 'list';

/** Where the board-or-list choice is remembered. Per browser, like the pane widths. */
const VIEW_KEY = 'reef.work.view';

function readViewMode(): WorkViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

/**
 * P0 first and unranked last, then the most recently changed.
 *
 * A column used to arrive newest-updated first, which put a P3 somebody had
 * just touched above a P0 nobody had — the one ordering a triage column must
 * not have. Unranked goes after P3 rather than before P0 because "not ranked
 * yet" is a fact about triage not having run, not a claim of urgency.
 */
const PRIORITY_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };

function priorityRank(row: WorkItemRow): number {
  return row.item.priority ? (PRIORITY_RANK[row.item.priority] ?? 4) : 5;
}

function byUrgency(left: WorkItemRow, right: WorkItemRow): number {
  const rank = priorityRank(left) - priorityRank(right);
  return rank !== 0 ? rank : right.updatedAt.localeCompare(left.updatedAt);
}

type SortKey = 'title' | 'stage' | 'priority' | 'status' | 'repo' | 'updated';

const COLUMNS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'title', label: 'Title' },
  { key: 'stage', label: 'Stage' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Run' },
  { key: 'repo', label: 'Repo' },
  { key: 'updated', label: 'Updated' },
];

/**
 * One ascending comparator per column; the header flips it. Stage sorts by the
 * factory's own order rather than by name, because "Implement before Pre-flight"
 * is the order work moves and the alphabet puts them the other way round.
 */
const SORTERS: Record<SortKey, (stages: Map<string, string>) => (left: WorkItemRow, right: WorkItemRow) => number> = {
  title: () => (left, right) => left.item.title.localeCompare(right.item.title),
  stage: (stages) => {
    const order = [...stages.keys()];
    const position = (row: WorkItemRow) => {
      const index = row.stageId ? order.indexOf(row.stageId) : -1;
      return index === -1 ? order.length : index;
    };
    return (left, right) => position(left) - position(right);
  },
  priority: () => (left, right) => priorityRank(left) - priorityRank(right),
  status: () => (left, right) => left.runStatus.localeCompare(right.runStatus),
  repo: () => (left, right) => (left.item.repo ?? '').localeCompare(right.item.repo ?? ''),
  updated: () => (left, right) => left.updatedAt.localeCompare(right.updatedAt),
};

function WorkCard({ row, selected, onOpen }: { row: WorkItemRow; selected: boolean; onOpen(): void }) {
  const item = row.item;
  return (
    <button type="button" className="work-card" aria-current={selected ? 'true' : undefined} onClick={onOpen}>
      <span className="work-card-title">{item.title}</span>
      <span className="work-card-meta">
        {item.priority && <span className={`pill ${priorityTone(item.priority)}`}>{item.priority}</span>}
        <span className={`run-dot ${runTone(row.runStatus)}`} title={`run ${row.runStatus}`} />
        <span className="hint">{row.runStatus.replace(/_/g, ' ')}</span>
        {item.repo && <span className="hint mono">{item.repo}</span>}
      </span>
      <span className="work-card-foot hint">
        {item.signal.source.replace(/_/g, ' ')} · {timeAgo(row.updatedAt)}
      </span>
    </button>
  );
}

/**
 * One item, with its run in it rather than a link to it.
 *
 * The drawer used to end at a "Run" link, so seeing which step was running,
 * what failed, or talking to a step meant leaving the board. The steps are
 * fetched here with `api.getRun` — the same call the run screen makes — and
 * drawn by the compact timeline; "Open chat" on a step goes to the run screen
 * with that step's chat open, and Pause/Cancel are the run screen's own
 * control call so a pause from here lands the same way as one from there.
 *
 * Mounted with `key={runId}` by the board, so opening another card is a fresh
 * drawer: a step list from the previous item cannot sit under the new title
 * while its own request is still out.
 */
function WorkItemDrawer({
  row,
  onClose,
  onRunChanged,
  go,
}: {
  row: WorkItemRow;
  onClose(): void;
  /** After a control took hold, so the card's status dot agrees with the drawer. */
  onRunChanged(): void;
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  const item: WorkItem = row.item;
  const [run, setRun] = useState<{ status: string; steps: StepRunSummary[] } | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [effect, setEffect] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .getRun(row.runId)
      .then((result) => current && setRun({ status: result.run.status, steps: result.steps }))
      .catch((caught) => current && setRunError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [row.runId]);

  // The run's own answer once it has arrived; the board's row until then.
  const status = run?.status ?? row.runStatus;

  const control = async (intent: 'cancel' | 'pause' | 'resume') => {
    setRunError(null);
    try {
      const result = await api.controlRun(row.runId, intent);
      setEffect(result.effect);
      /*
       * Re-read rather than infer, as the run screen does: resuming a run that
       * was awaiting approval goes back to awaiting approval, and cancelling
       * moves nothing until the current step reaches a safe point.
       */
      const detail = await api.getRun(row.runId);
      setRun({ status: detail.run.status, steps: detail.steps });
      onRunChanged();
    } catch (caught) {
      setRunError((caught as Error).message);
    }
  };

  return (
    <aside className="drawer" aria-label="Work item">
      <header className="drawer-head">
        <div>
          <span className="eyebrow">
            {item.id} · {item.state.replace(/_/g, ' ')}
          </span>
          <h2>{item.title}</h2>
        </div>
        <button className="ghost" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </header>

      <div className="drawer-body">
        {item.description && <p className="prose">{item.description}</p>}

        <dl className="facts-grid">
          <div>
            <dt>Priority</dt>
            <dd>{item.priority ? <span className={`pill ${priorityTone(item.priority)}`}>{item.priority}</span> : <span className="hint">not ranked yet</span>}</dd>
          </div>
          <div>
            <dt>Confidence</dt>
            <dd>{item.confidence}%</dd>
          </div>
          <div>
            <dt>Repository</dt>
            <dd>{item.repo ?? <span className="hint">not chosen</span>}</dd>
          </div>
          <div>
            <dt>Owned next by</dt>
            <dd>{item.assignedTo ?? <span className="hint">—</span>}</dd>
          </div>
          <div>
            <dt>Came from</dt>
            <dd>
              {item.signal.source.replace(/_/g, ' ')} · <code>{item.signal.externalId}</code>
              {item.signal.url && (
                <>
                  {' '}
                  <a href={item.signal.url} target="_blank" rel="noreferrer" className="linkish">
                    open <Icon name="external" />
                  </a>
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>Workflow</dt>
            <dd>
              <button className="linkish" onClick={() => go({ tab: 'runs', workflowId: row.workflowId, runId: row.runId })}>
                {row.workflowName}
              </button>
            </dd>
          </div>
          <div>
            <dt>Last change</dt>
            <dd>
              {timeAgo(row.updatedAt)}
              {row.updatedBy && <span className="hint"> by {row.updatedBy}</span>}
            </dd>
          </div>
          {item.duplicateOf && (
            <div>
              <dt>Duplicate of</dt>
              <dd>
                <code>{item.duplicateOf}</code>
              </dd>
            </div>
          )}
        </dl>

        <section className="drawer-section drawer-run" aria-label="Run">
          <div className="row wrap">
            <h3 style={{ flex: 1, minWidth: 0 }}>Run</h3>
            {/* Only while the run can still be asked to leave; a Pause on a finished run would 4xx. */}
            {LIVE.has(status) && (
              <>
                <button onClick={() => void control(status === 'paused' ? 'resume' : 'pause')}>
                  {status === 'paused' ? 'Resume' : 'Pause'}
                </button>
                <button className="ghost danger" onClick={() => void control('cancel')}>
                  Cancel
                </button>
              </>
            )}
          </div>
          {effect && (
            <div className="banner warn">
              <p style={{ margin: 0 }}>{effect}</p>
            </div>
          )}
          {runError && <div className="banner danger">{runError}</div>}
          {run ? (
            <CompactRunTimeline
              steps={run.steps}
              status={run.status}
              onOpenStep={(step) =>
                go({ tab: 'runs', workflowId: row.workflowId, runId: row.runId, stepId: step.stepId })
              }
            />
          ) : (
            !runError && <p className="hint" style={{ margin: 0 }}>Opening run…</p>
          )}
        </section>

        {item.change && (
          <section className="drawer-section">
            <h3>Change</h3>
            <p className="prose">{item.change.summary}</p>
            <dl className="facts-grid">
              <div>
                <dt>Pull request</dt>
                <dd>
                  {item.change.url ? (
                    <a href={item.change.url} target="_blank" rel="noreferrer" className="linkish">
                      #{item.change.pullRequest} <Icon name="external" />
                    </a>
                  ) : (
                    <span className="hint">not opened</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Branch</dt>
                <dd>
                  <code>{item.change.branch}</code>
                </dd>
              </div>
              <div>
                <dt>Files</dt>
                <dd>{item.change.filesTouched.length}</dd>
              </div>
              <div>
                <dt>Tests added</dt>
                <dd>{item.change.testsAdded.length}</dd>
              </div>
            </dl>
          </section>
        )}

        {item.verdict && (
          <section className="drawer-section">
            <h3>Verdict</h3>
            <p className="hint">{item.verdict.approved ? 'Passed the gate.' : 'Did not pass the gate.'}</p>
            <ul className="verdicts">
              {item.verdict.dimensions.map((dimension) => (
                <li key={dimension.dimension}>
                  <span className={`pill ${dimension.verdict === 'pass' ? 'ok' : dimension.verdict === 'fail' ? 'danger' : 'warn'}`}>
                    {dimension.verdict}
                  </span>
                  <strong>{dimension.dimension}</strong>
                  <span className="hint">{dimension.confidence}% sure</span>
                  {dimension.findings.length > 0 && (
                    <ul>
                      {dimension.findings.map((finding) => (
                        <li key={finding}>{finding}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            {item.verdict.escalationReason && (
              <div className="banner warn">{item.verdict.escalationReason}</div>
            )}
          </section>
        )}

        {item.release && (
          <section className="drawer-section">
            <h3>Release</h3>
            <dl className="facts-grid">
              <div>
                <dt>Version</dt>
                <dd>
                  <code>{item.release.version}</code>
                </dd>
              </div>
              <div>
                <dt>Deployed</dt>
                <dd>{item.release.deployedAt ? new Date(item.release.deployedAt).toLocaleString() : '—'}</dd>
              </div>
              <div>
                <dt>Healthy</dt>
                <dd>{item.release.healthy == null ? '—' : item.release.healthy ? 'yes' : 'no'}</dd>
              </div>
              {item.release.rolledBack && (
                <div>
                  <dt>Rolled back</dt>
                  <dd>{item.release.rollbackReason ?? 'yes'}</dd>
                </div>
              )}
            </dl>
            <pre className="prompt">{item.release.changelog}</pre>
          </section>
        )}
      </div>
    </aside>
  );
}

/** Statuses a run can still be asked to leave. Mirrors `LIVE_RUN_STATUSES`, as the run screen does. */
const LIVE = new Set(['pending', 'running', 'awaiting_approval', 'paused']);

function priorityTone(priority: string): string {
  return priority === 'P0' ? 'danger' : priority === 'P1' ? 'warn' : '';
}

function runTone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'awaiting_approval' || status.startsWith('paused')) return 'warn';
  return 'accent';
}

export function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
